<template>
  <div class="image-uploader-container">
    <!-- 图片上传框 -->
    <div v-if="!imageUrl"
         :style="{ width: boxWidth + 'px', height: boxHeight + 'px' }"
         class="upload-box upload-area"
         :class="{ 'is-uploading': uploading }"
         @click="handleUploadClick"
    >
      <div v-if="uploading" class="upload-loading">
        <a-spin tip="上传中..." :size="40" />
      </div>
      <div v-else class="upload-hint">
        <plus-outlined class="plus-icon" />
        <p class="hint-text">{{ uploadText }}</p>
      </div>
    </div>

    <!-- 已上传的图片 -->
    <div v-else
         :style="{ width: boxWidth + 'px', height: boxHeight + 'px' }"
         class="upload-box"
    >
      <img
          :src="displayUrl"
          alt="上传的图片"
          class="preview-img"
          @dblclick="handleImageDblClick"
      >
      <div v-if="!onlyPreview" class="delete-btn" @click.stop="handleDelete">
        <delete-outlined />
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <a-modal
        v-model:open="previewVisible"
        :footer="null"
        :width="800"
    >
      <div class="preview-modal-content">
        <img :src="displayUrl" alt="预览图片" class="modal-image">
      </div>
    </a-modal>

    <!-- 隐藏的上传组件 -->
    <a-upload
        name="file"
        :action="''"
        :beforeUpload="beforeUpload"
        :showUploadList="false"
        accept=".jpg,.jpeg,.png,.gif"
        ref="uploadRef"
        :multiple="false"
    />
  </div>
</template>

<script setup>
import { ref, watch, onBeforeUnmount, defineProps, defineEmits, defineExpose } from 'vue';
import { Upload, message, Spin } from 'ant-design-vue';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons-vue';
import { uploadOneFile } from '@/api/upload.js';
import { previewFile } from '@/api/file.js';

// 定义属性
const props = defineProps({
  // 图片路径
  imageUrl: {
    type: String,
    default: ''
  },
  // 上传路径名称
  pathName: {
    type: String,
    default: 'image'
  },
  // 上传框宽度
  boxWidth: {
    type: Number,
    default: 150
  },
  // 上传框高度
  boxHeight: {
    type: Number,
    default: 150
  },
  // 是否仅预览
  onlyPreview: {
    type: Boolean,
    default: false
  },
  // 上传提示文字
  uploadText: {
    type: String,
    default: '点击上传图片'
  }
});

// 定义事件
const emits = defineEmits(['update:imageUrl', 'uploadSuccess', 'delete']);

// 引用
const uploadRef = ref(null);
// 上传中状态
const uploading = ref(false);

// 预览相关状态
const previewVisible = ref(false);
// 用于回显/预览的 blob URL（通过 previewFile 接口获取）
const displayUrl = ref('');

// 通过 previewFile 接口获取图片 blob 并生成临时 URL
const loadDisplayImage = async (url) => {
  if (!url) {
    if (displayUrl.value) {
      window.URL.revokeObjectURL(displayUrl.value);
      displayUrl.value = '';
    }
    return;
  }
  try {
    const response = await previewFile(url);
    if (response && (response instanceof Blob || response instanceof ArrayBuffer)) {
      const blob = response instanceof ArrayBuffer ? new Blob([response]) : response;
      if (displayUrl.value) window.URL.revokeObjectURL(displayUrl.value);
      displayUrl.value = window.URL.createObjectURL(blob);
    }
  } catch (error) {
    console.error('图片加载失败:', error);
  }
};

watch(() => props.imageUrl, (val) => {
  loadDisplayImage(val);
}, { immediate: true });

onBeforeUnmount(() => {
  if (displayUrl.value) window.URL.revokeObjectURL(displayUrl.value);
});

// 处理上传点击
const handleUploadClick = () => {
  if (uploading.value) return;
  if (uploadRef.value && !props.imageUrl) {
    // 触发文件输入框的点击事件
    uploadRef.value.$el.querySelector('input[type="file"]').click();
  }
};

// 处理删除
const handleDelete = () => {
  emits('update:imageUrl', '');
  emits('delete');
};

// 处理图片双击事件
const handleImageDblClick = () => {
  previewVisible.value = true;
};

// 上传前处理
const beforeUpload = (file) => {
  // 检查文件类型
  const isImage = /\.(jpg|jpeg|png|gif)$/i.test(file.name);
  if (!isImage) {
    message.error('仅支持 JPG、JPEG、PNG、GIF 格式的图片!');
    return Upload.LIST_IGNORE;
  }

  // 检查文件大小
  const isLt2M = file.size / 1024 / 1024 < 5;
  if (!isLt2M) {
    message.error('图片大小不能超过 5MB!');
    return Upload.LIST_IGNORE;
  }

  // 上传文件
  uploading.value = true;
  const formData = convertFormData(file);
  uploadOneFile(formData)
      .then(res => {
        if (res && res.data) {
          emits('update:imageUrl', res.data);
          emits('uploadSuccess', res.data);
          message.success('上传成功');
        }
      })
      .catch(err => {
        message.error(`上传失败: ${err.message || '未知错误'}`);
      })
      .finally(() => {
        uploading.value = false;
      });

  return false; // 阻止自动上传
};

// 转换为FormData
const convertFormData = (file) => {
  const folder = Date.now().toString();
  const json = {
    bucketName: 'testing-management-private',
    pathName: props.pathName + '/' + folder, // 上传服务器路径
  };
  const formData = new FormData();
  formData.append('uploadFile', file);
  formData.append('json', new Blob([JSON.stringify(json)], { type: 'application/json' }));
  return formData;
};

// 导出组件引用
defineExpose({
  handleDelete,
  clear: () => {
    emits('update:imageUrl', '');
  }
});
</script>

<style lang="less" scoped>
.image-uploader-container {
  .upload-box {
    position: relative;
    border: 2px dashed #d9d9d9;
    border-radius: 8px;
    overflow: hidden;
    transition: all 0.3s;

    &:hover {
      border-color: #1890ff;
    }

    &.is-uploading {
      border-color: #1890ff;
      cursor: not-allowed;
    }

    .upload-loading {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .preview-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      cursor: pointer;
      transition: transform 0.3s;

      &:hover {
        transform: scale(1.05);
      }
    }

    .delete-btn {
      position: absolute;
      top: 5px;
      right: 5px;
      width: 24px;
      height: 24px;
      background-color: rgba(0, 0, 0, 0.5);
      color: white;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;

      &:hover {
        background-color: rgba(0, 0, 0, 0.7);
      }
    }
  }

  .upload-area {
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  }

  .upload-hint {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 10px;

    .plus-icon {
      font-size: 32px;
      color: #1890ff;
      margin-bottom: 8px;
    }

    .hint-text {
      color: #999;
      font-size: 14px;
      text-align: center;
    }
  }
}

/* 预览弹窗样式 */
.preview-modal-content {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;

  .modal-image {
    max-width: 100%;
    max-height: 600px;
    object-fit: contain;
  }
}
</style>